import { createSelector } from 'reselect';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import differenceBy from 'lodash/differenceBy';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import qs from 'qs';

import http from '../http';
import intl from '../../shared/intl';
import endpoints from '../../../config/endpoints';
import { createSelectOptionsFromSysEnums } from '../form-elements/select';

import { actions as rootActions } from '../../root';
import _values from 'lodash/values';

const defaultInitialState = {
	listOfFilters: [],
	selectedFilters: [],
	blockedFilterNames: null,
	blockedFilters: [],
	showListOfFilters: false,
	shouldOpenOptionsByName: ''
};

const createFilters = ({
	namespace,
	stateNodeName,
	listOfFilters,
	needTakeInitialFiltersFromQuery = false,
	queryBuildFunc = () => ''
}) => {
	// action types
	const SHOW_FILTERS_LIST = `${namespace}/${stateNodeName}-show-list`;
	const HIDE_FILTERS_LIST = `${namespace}/${stateNodeName}-hide-list`;
	const ADD_FILTER = `${namespace}/${stateNodeName}-add-filter`;
	const REINITIALIZE_FILTERS = `${namespace}/${stateNodeName}-REINITIALIZE_FILTERS`;
	const DELETE_FILTER = `${namespace}/${stateNodeName}-delete-filter`;
	const DELETE_FILTER_BY_NAME = `${namespace}/${stateNodeName}-delete-filter-by-name`;
	const ADD_FILTER_VALUE = `${namespace}/${stateNodeName}-add-filter-value`;
	const DELETE_FILTER_VALUE = `${namespace}/${stateNodeName}-delete-filter-value`;
	const CLEAR_FILTER_VALUE = `${namespace}/${stateNodeName}-clear-filter-value`;
	const TOGGLE_INVERSE_FILTER_VALUE = `${namespace}/${stateNodeName}-toggle-inverse-filter-value`;
	const DELETE_ALL_FILTERS = `${namespace}/${stateNodeName}-delete-all-filters`;
	const INITIALIZE_FILTERS = `${namespace}/${stateNodeName}-INITIALIZE_FILTERS`;

	const OPEN_OPTIONS = `${namespace}/${stateNodeName}-open-options`;
	const CLOSE_OPTIONS = `${namespace}/${stateNodeName}-close-options`;

	const FETCH_SUCCESS_FILTER_OPTIONS = `${namespace}/${stateNodeName}-fetch-success-filter-options`;
	const FETCH_ERROR_FILTER_OPTIONS = `${namespace}/${stateNodeName}-fetch-error-filter-options`;

	const TOGGLE_BLOCK_FILTER_SUCCESS = `${namespace}/${stateNodeName}-toggle-block-success`;
	const TOGGLE_BLOCK_FILTER_ERROR = `${namespace}/${stateNodeName}-toggle-block-error`;

	// init state
	const initialState = {
		...defaultInitialState,
		listOfFilters: listOfFilters
	};

	initialState.selectedFilters = initialState.listOfFilters;

	// actions
	const actions = {
		showFiltersList: () => ({
			type: SHOW_FILTERS_LIST
		}),
		reinitializeFilters: () => ({
			type: REINITIALIZE_FILTERS,
			payload: needTakeInitialFiltersFromQuery ? getInitialFiltersFromQuery(listOfFilters) : []
		}),
		hideFiltersList: () => ({
			type: HIDE_FILTERS_LIST
		}),
		addFilter: (filterName, shouldOpenOptions = true) => dispatch => {
			dispatch({
				type: ADD_FILTER,
				payload: filterName
			});

			shouldOpenOptions && dispatch(actions.openFilterOptionsByName(filterName));
		},
		initializeFiltersFromQueryObject: (query) => ({
			type: INITIALIZE_FILTERS,
			payload: getFiltersFromQueryObject(query, listOfFilters)
		}),
		deleteFilter: () => (dispatch) => {
			dispatch({ type: DELETE_FILTER });
			dispatch(actions.refreshSelectedFilters());
		},
		deleteFilterByName: filterName => (dispatch, getState) => {
			dispatch(actions.toggleFilterBlock(filterName, false));
			dispatch({
				type: DELETE_FILTER_BY_NAME,
				payload: filterName
			});
			dispatch(actions.refreshSelectedFilters());
		},
		addFilterValue: (value, filterName, isRefreshNeeded = true) => (dispatch) => {
			dispatch({
				type: ADD_FILTER_VALUE,
				payload: { value, filterName }
			});
			if (isRefreshNeeded) {
				dispatch(actions.refreshSelectedFilters());
			}
		},
		deleteFilterValue: (item, filterName) => (dispatch) => {
			dispatch({
				type: DELETE_FILTER_VALUE,
				payload: {
					item,
					filterName
				}
			});
			dispatch(actions.refreshSelectedFilters());
		},
		clearFilterValue: filterName => (dispatch) => {
			dispatch({
				type: CLEAR_FILTER_VALUE,
				payload: filterName
			});
			dispatch(actions.refreshSelectedFilters());
		},
		toggleInverseFilterValue: filterName => (dispatch) => {
			dispatch({
				type: TOGGLE_INVERSE_FILTER_VALUE,
				payload: filterName
			});
			dispatch(actions.refreshSelectedFilters());
		},
		loadFilterOptions: options => (dispatch, getState) => text => {
			const { type, source, isTranslateLabels } = options;

			if (type === 'dynamic') {
				const state = getState()[namespace];

				const query = `${queryBuildFunc(state)}&${qs.stringify({ query: text })}`
					+ (options.params ? `&${options.params}` : '');

				if (document.fiterIsLoading) {
					return;
				}
				document.fiterIsLoading = true;

				return loadOptionsFromLookup({
					source: source.split('?')[0],
					query: qs.stringify({
						...qs.parse(query, { ignoreQueryPrefix: true }),
						...qs.parse(source.split('?')[1])
					}),
					dispatch,
					actions: [
						FETCH_SUCCESS_FILTER_OPTIONS,
						FETCH_ERROR_FILTER_OPTIONS
					]
				}).then((action) => {
					document.fiterIsLoading = false;

					return action;
				});
			}

			if (type === 'static') {
				const lists = get(getState(), source);
				const search = lists.filter(item => text ? item.text.toLowerCase().indexOf(text.toLowerCase()) !== -1 : item);
				const options = createSelectOptionsFromSysEnums(search);
				if (isTranslateLabels) {
					const Intl = intl(getState());
					return Promise.resolve({
						options: options.map(i => {i.label = Intl(i.label); return i;})
					});
				}
				return Promise.resolve({ options });
			}

			if (type === 'provided') {
				return Promise.resolve({ options: source });
			}

			if (type === 'from_search') {
				return Promise.resolve({ options: [{ value: text, label: text }] });
			}
		},

		deleteAllFilters: () => (dispatch, getState) => {
			const filtersBlockedState = getState()[namespace][stateNodeName].selectedFilters;
			filtersBlockedState.forEach(filter => {
				dispatch(actions.toggleFilterBlock(filter.name, false));
				dispatch({
					type: DELETE_FILTER_BY_NAME,
					payload: filter.name
				});
			});
			dispatch({
				type: DELETE_ALL_FILTERS
			});
			dispatch(actions.refreshSelectedFilters());
		},

		openFilterOptionsByName: name => ({
			type: OPEN_OPTIONS,
			payload: name
		}),

		refreshSelectedFilters: () => (dispatch, getState) => {
			const selectedFilters = getState()[namespace][stateNodeName].selectedFilters;
			const filtersProcessed = selectedFilters.reduce((acc, value) => {
				acc[value.name] = value.value;
				return acc;
			}, {});

			filtersProcessed.query = {
				filters: selectedFilters.filter((f) => !f.shouldInverse).reduce((acc, value) => {
					acc[value.name] = value.value.map((v) => v.value);
					return acc;
				}, {}),
				inverse_filters: selectedFilters.filter((f) => f.shouldInverse).reduce((acc, value) => {
					acc[value.name] = value.value.map((v) => v.value);
					return acc;
				}, {}),
				...filtersProcessed.query
			};
			filtersProcessed.entity = {
				filters: selectedFilters.map((filter) => ({
					is_inverted: filter.shouldInverse.toString(),
					values: filter.value,
					name: filter.name
				})),
				...filtersProcessed.entity
			};

			const newUrl = window.location.origin + window.location.pathname + `?${qs.stringify(filtersProcessed)}`;

			history.replaceState({}, '', newUrl);
		},

		closeFilterOptionsByName: name => ({
			type: CLOSE_OPTIONS,
			payload: name
		}),

		toggleFilterBlock: (name, shouldBlock) => (dispatch, getState) => {
			const filtersState = getState()[namespace][stateNodeName];
			let { selectedFilters, blockedFilterNames } = filtersState;

			if (!blockedFilterNames) {
				let savedSettings = selectors.getSavedFilters(getState());
				blockedFilterNames = savedSettings.entity ? savedSettings.entity.filters.map(f => f.name) : [];
			}

			const query = {
				filters: {},
				inverse_filters: {}
			};

			selectedFilters
				.filter(f => blockedFilterNames.indexOf(f.name) !== -1 || f.name === name)
				.filter(f => shouldBlock ? true : f.name !== name)
				.forEach(filter => {
					query[filter.shouldInverse ? 'inverse_filters' : 'filters'][filter.name] = filter.value.map(obj => obj.value);
				});

			const entity = {
				filters: selectedFilters
					.filter(f => blockedFilterNames.indexOf(f.name) !== -1 || f.name === name)
					.filter(f => shouldBlock ? true : f.name !== name)
					.map(filter => ({ name: filter.name, values: filter.value, is_inverted: filter.shouldInverse }))
			};

			const request = http({
				url: `${endpoints.ADMIN_PROFILE}/saved-filters`,
				method: 'put',
				actions: [
					TOGGLE_BLOCK_FILTER_SUCCESS,
					TOGGLE_BLOCK_FILTER_ERROR
				],
				data: {
					title: namespace,
					settings: { query, entity }
				},
				addToResponse: entity.filters.map(f => f.name)
			});

			return request(dispatch).then(action => {
				if (action.type === TOGGLE_BLOCK_FILTER_SUCCESS) {
					dispatch(rootActions.refetchSavedFilters());
				}
			});
		},

		setFiltersFromExternalQuery: (externalFilters, isRefreshNeeded = true) => (dispatch, getState) => {
			const { filters: { selectedFilters, listOfFilters } } = getState()[namespace];

			externalFilters.forEach(entity => {
				if (
					find(listOfFilters, { name: entity.name }) &&
					!find(selectedFilters, { name: entity.name })
				) {
					dispatch(actions.addFilter(entity.name));
					dispatch(actions.addFilterValue(entity.values || [], entity.name, isRefreshNeeded));
					if (entity.is_inverted && entity.is_inverted !== 'false') { // qs.stringify(false) => 'false'
						dispatch(actions.toggleInverseFilterValue(entity.name));
					}
				}
			});
		},

		prepareFiltersList: filtersList => (dispatch, getState) => text => {
			const formatter = entity => ({ label: entity.title, value: entity.name });
			const search = text => entity => {
				if (!text) return entity;
				return entity.label.toLowerCase().indexOf(text.toLowerCase()) !== -1;
			};

			const options = filtersList.map(formatter).filter(search(text));

			return Promise.resolve({ options });
		}
	};

	// reducers
	const reducers = {
		[SHOW_FILTERS_LIST]: state => {
			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					showListOfFilters: true
				}
			};
		},
		[HIDE_FILTERS_LIST]: state => {
			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					showListOfFilters: false
				}
			};
		},
		[ADD_FILTER]: (state, action) => {
			const { listOfFilters, selectedFilters } = state[stateNodeName];
			const filtered = find(listOfFilters, { 'name': action.payload });
			const selected = [].concat(selectedFilters);

			selected.push(filtered);

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters: selected,
					showListOfFilters: false
				}
			};
		},
		[DELETE_FILTER]: state => {
			const { selectedFilters } = state[stateNodeName];
			const selected = [].concat(selectedFilters);

			selected.pop();

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters: selected
				}
			};
		},
		[DELETE_FILTER_BY_NAME]: (state, action) => {
			const selected = state[stateNodeName].selectedFilters;
			const selectedFilters = selected.filter(
				item => item.name !== action.payload
			);

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters
				}
			};
		},
		[DELETE_ALL_FILTERS]: state => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				selectedFilters: []
			}
		}),
		[REINITIALIZE_FILTERS]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName], selectedFilters: action.payload
			}
		}),
		[INITIALIZE_FILTERS]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName], selectedFilters: action.payload
			}
		}),
		[ADD_FILTER_VALUE]: (state, action) => {
			const { value, filterName } = action.payload;
			const selectedFilters = cloneDeep(state[stateNodeName].selectedFilters).map(filter => {
				if (filter.name === filterName) {
					filter.value = value;
				}
				return filter;
			});

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters,
					shouldOpenOptionsByName: ''
				}
			};
		},
		[DELETE_FILTER_VALUE]: (state, action) => {
			const { item, filterName } = action.payload;
			const selectedFilters = cloneDeep(state[stateNodeName].selectedFilters).map(filter => {
				if (filter.name === filterName) {
					filter.value = differenceBy(filter.value, [item], 'value');
				}
				return filter;
			});

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters
				}
			};
		},
		[CLEAR_FILTER_VALUE]: (state, action) => {
			const selectedFilters = cloneDeep(state[stateNodeName].selectedFilters).map(filter => {
				if (filter.name === action.payload) {
					filter.value = [];
				}
				return filter;
			});

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters
				}
			};
		},
		[TOGGLE_INVERSE_FILTER_VALUE]: (state, action) => {
			const selectedFilters = cloneDeep(state[stateNodeName].selectedFilters).map(filter => {
				if (filter.name === action.payload) {
					filter.shouldInverse = !filter.shouldInverse;
				}
				return filter;
			});

			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					selectedFilters
				}
			};
		},

		[OPEN_OPTIONS]: (state, action) => {
			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					shouldOpenOptionsByName: action.payload
				}
			};
		},

		[CLOSE_OPTIONS]: (state, action) => {
			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					shouldOpenOptionsByName: ''
				}
			};
		},

		[TOGGLE_BLOCK_FILTER_SUCCESS]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				blockedFilterNames: action.meta
			}
		}),

		[TOGGLE_BLOCK_FILTER_ERROR]: state => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				blockedFilterNames: null
			}
		})
	};

	// selectors
	const selectors = {
		getFilters: (filtersSelector, appState) => createSelector(
			filtersSelector,
			appState,
			(filters, appState) => {
				let { listOfFilters, selectedFilters, blockedFilterNames } = filters;

				if (!blockedFilterNames) {
					let savedSettings = selectors.getSavedFilters(appState);
					blockedFilterNames = savedSettings.entity ? savedSettings.entity.filters.map(f => f.name) : [];
				}

				return {
					...filters,
					selectedFilters: filters.selectedFilters.map(filter => {
						if (blockedFilterNames && blockedFilterNames.indexOf(filter.name) !== -1) {
							return { ...filter, isBlocked: true };
						}
						return { ...filter, isBlocked: false };
					}),
					listOfFilters: differenceBy(
						listOfFilters,
						selectedFilters,
						'name'
					)
				};
			}
		),
		getSavedFilters: state => {
			if (state.root.savedFilters.length) {
				let namespaceFilters = find(state.root.savedFilters, { title: namespace });
				let settings = namespaceFilters && namespaceFilters.settings;
				return settings || {};
			}
			return {};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const loadOptionsFromLookup = options => {
	const { source, query, dispatch, actions } = options;
	const request = http({
		url: `${source}?${query}`,
		method: 'get',
		actions
	});

	return request(dispatch).then(action => {
		if (action.type === actions[0]) {
			return {
				options: createSelectOptionsFromSysEnums(
					action.payload.data.filter(entity => !!entity.id || !!entity)
				)
			};
		}
	});
};

export function getFiltersObject(filters, isInverse = false) {
	return reduce(filters.selectedFilters, (result, filter) => {
		if (filter.value.length && filter.shouldInverse === isInverse) {
			result[filter.name] = filter.value.map((f) => f.value);
		}
		return result;
	}, {});
}

function getInitialFiltersFromQuery(listOfFilters) {
	const filtersObject = qs.parse(window.location.search.replace('?', '')) || [];

	const filtersArray = Object.keys(filtersObject).reduce((acc, val) => {
		return [...acc, {...find(listOfFilters, { 'name': val }), value: filtersObject[val] } ];
	}, []);

	return filtersArray;
}

function getFiltersFromQueryObject(queryObject, listOfFilters) {

	const filtersArray = Object.keys(queryObject).reduce((acc, val) => {
		return [...acc, {...find(listOfFilters, { 'name': val }), value: _values(queryObject[val]) } ];
	}, []);

	return filtersArray;
}

export default createFilters;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/shared/fixed-filters/index.js